import {
  BusinessContext,
  CrmDefinition,
  ImportRow,
  LookupBusiness,
  LookupUser,
  LookupWorkspace,
  PushEstimate,
  PushPreview,
  ReviewResponse,
  TestConnectionResult,
} from "./crmImport.types";

export const fixtureCrms: CrmDefinition[] = [
  {
    id: "servicetitan",
    name: "ServiceTitan",
    description: "Jobs, photos & job types",
    enabled: true,
    comingSoon: false,
    credentialFields: [
      { key: "tenantId", label: "Tenant ID", placeholder: "e.g. 1498276033", secret: false },
      { key: "appKey", label: "App Key", placeholder: "ak1.xxxxxxxx", secret: false },
      { key: "clientId", label: "Client ID", placeholder: "cid.xxxxxxxx", secret: false },
      { key: "clientSecret", label: "Client Secret", placeholder: "cs1.xxxxxxxx", secret: true },
    ],
    instructions: [
      "In ServiceTitan, go to Settings > Integrations > API Application Access.",
      "Open the Map Ranking app and copy the Client ID and Client Secret.",
      "The Tenant ID and App Key are shown on the same app page.",
    ],
    requiredScopes: ["scope.string.one:r", "scope.string.two:r"],
  },
  {
    id: "companycam",
    name: "CompanyCam",
    description: "Projects & photos",
    enabled: true,
    comingSoon: false,
    credentialFields: [
      { key: "accessToken", label: "Access Token", placeholder: "Paste the CompanyCam access token", secret: true },
    ],
    instructions: [
      "Only a CompanyCam Admin or Manager can create an access token.",
      "Open the setup page with the link below, or go to Integrations, then Access Tokens, then New Personal Access Token.",
      "Enter a Name such as Map Ranking import and leave Permissions on Read only.",
      "Set Expiration to a period that covers the import, such as 30 days, instead of No expiration.",
      "Click Create token, copy it right away, and paste it here.",
    ],
    helpUrl: "https://app.companycam.com/access-keys/api/new",
    completedFilter: false,
    supportsPhotoJobs: true,
    requiredScopes: [],
  },
  { id: "jobber", name: "Jobber", description: "Jobs & visit photos", enabled: false, comingSoon: true },
  {
    id: "housecallpro",
    name: "Housecall Pro",
    description: "Jobs & attachments",
    enabled: true,
    comingSoon: false,
    credentialFields: [
      { key: "apiKey", label: "API Key", placeholder: "Paste the Housecall Pro API key", secret: true },
    ],
    instructions: [
      "Requires a Housecall Pro MAX plan.",
      "In Housecall Pro open App Store, then My Apps, then API Key Management.",
      "Generate an API key and paste it here.",
    ],
    requiredScopes: [],
  },
];

export const fixtureUsers: LookupUser[] = [
  { id: "6700000000000000000000a1", email: "eagle@example.com", name: "Tom Eagle", workspaceCount: 1, businessCount: 1 },
  { id: "6700000000000000000000a2", email: "owner@example.com", name: "Marco Ruiz", workspaceCount: 2, businessCount: 3 },
];

export const fixtureWorkspaces: LookupWorkspace[] = [
  { id: "6701000000000000000000b1", name: "Eagle Plumbing", role: "owner", businessCount: 1 },
];

export const fixtureBusinesses: LookupBusiness[] = [
  {
    id: "6702000000000000000000c1",
    name: "Eagle Plumbing Repairs and Drain Cleaning",
    ownerName: "Tom Eagle",
    status: "active",
    timeZone: "America/New_York",
    profileComplete: true,
  },
  {
    id: "6702000000000000000000c2",
    name: "Eagle Plumbing Annex",
    ownerName: "Tom Eagle",
    status: "suspended",
    timeZone: "America/New_York",
    profileComplete: true,
  },
];

export const fixtureContext: BusinessContext = {
  business: {
    id: "6702000000000000000000c1",
    name: "Eagle Plumbing Repairs and Drain Cleaning",
    status: "active",
    timeZone: "America/Denver",
    hasOfficeLocation: true,
  },
  owner: { id: "6700000000000000000000a1", name: "Tom Eagle", email: "eagle@example.com" },
  employees: [
    { id: "6700000000000000000000a1", label: "Tom Eagle (Owner)", isOwner: true, status: "active", moderation: false },
    { id: "6703000000000000000000d1", label: "Jordan P.", isOwner: false, status: "active", moderation: true },
  ],
  defaultEmployeeId: "6700000000000000000000a1",
  widgets: [
    { id: "6704000000000000000000e1", name: "Homepage Work Feed", kind: "map" },
    { id: "6705000000000000000000e2", name: "Project Gallery", kind: "photo_gallery" },
  ],
  categories: [
    { id: "6706000000000000000000f1", name: "Plumbing" },
    { id: "6706000000000000000000f2", name: "Drain Cleaning" },
  ],
  warnings: [],
};

export const fixtureTestConnection: TestConnectionResult = {
  ok: true,
  tenantId: "1498276033",
  jobsFound: 12000,
  checks: [
    { key: "auth", ok: true, message: "Credentials accepted" },
    { key: "jobs", ok: true, message: "Jobs readable" },
    { key: "jobTypes", ok: true, message: "Job types readable" },
    { key: "locations", ok: true, message: "Locations readable" },
    { key: "attachments", ok: true, message: "Photos readable" },
  ],
  scopes: { required: ["scope.string.one:r"], granted: ["scope.string.one:r"], missing: [] },
};

const baseRow = {
  crm: "servicetitan",
  crmLabel: "ServiceTitan",
  business: { id: "6702000000000000000000c1", name: "Eagle Plumbing Repairs and Drain Cleaning" },
  owner: { email: "eagle@example.com", name: "Tom Eagle" },
  warnings: [],
  error: null,
};

export const fixtureImports: ImportRow[] = [
  {
    ...baseRow,
    id: "6720000000000000000000a1",
    seq: 1042,
    kind: "pull",
    status: "running",
    stage: "images",
    stageIndex: 1,
    createdAt: "2026-10-16T09:11:00.000Z",
    startedAt: "2026-10-16T09:11:04.000Z",
    finishedAt: null,
    updatedAt: "2026-10-16T09:40:12.000Z",
    progress: { percent: 52.4 },
    pull: {
      totalJobs: 12000,
      fetchedJobs: 12000,
      totalPhotos: 38410,
      cachedPhotos: 21480,
      skippedPhotos: 0,
      processedPhotos: 21480,
      failedPhotos: 3,
      skippedNoPhotos: 412,
      sheetRows: 0,
      rangePreset: "all",
      rangeFrom: null,
      rangeTo: null,
      sheetPercent: 0,
      sheetReady: false,
      sheetExpiresAt: null,
    },
    push: null,
    actions: { canDownload: false, canRetry: false, canCancel: true, canDelete: false, canUseForPush: false },
  },
  {
    ...baseRow,
    id: "6720000000000000000000a2",
    seq: 1039,
    kind: "pull",
    status: "done",
    stage: null,
    stageIndex: null,
    createdAt: "2026-10-14T09:11:00.000Z",
    startedAt: "2026-10-14T09:11:04.000Z",
    finishedAt: "2026-10-14T10:02:00.000Z",
    updatedAt: "2026-10-14T10:02:00.000Z",
    progress: { percent: 100 },
    pull: {
      totalJobs: 3214,
      fetchedJobs: 3214,
      totalPhotos: 9800,
      cachedPhotos: 9800,
      skippedPhotos: 0,
      processedPhotos: 9800,
      failedPhotos: 0,
      skippedNoPhotos: 120,
      sheetRows: 3214,
      rangePreset: "all",
      rangeFrom: null,
      rangeTo: null,
      sheetPercent: 100,
      sheetReady: true,
      sheetExpiresAt: "2026-11-13T10:02:00.000Z",
    },
    push: null,
    actions: { canDownload: true, canRetry: false, canCancel: false, canDelete: true, canUseForPush: true },
  },
  {
    ...baseRow,
    id: "6720000000000000000000a3",
    seq: 1036,
    kind: "pull",
    status: "failed",
    stage: "jobs",
    stageIndex: 0,
    createdAt: "2026-10-12T09:11:00.000Z",
    startedAt: "2026-10-12T09:11:04.000Z",
    finishedAt: "2026-10-12T09:12:00.000Z",
    updatedAt: "2026-10-12T09:12:00.000Z",
    progress: { percent: 4 },
    pull: {
      totalJobs: 0,
      fetchedJobs: 0,
      totalPhotos: 0,
      cachedPhotos: 0,
      skippedPhotos: 0,
      processedPhotos: 0,
      failedPhotos: 0,
      skippedNoPhotos: 0,
      sheetRows: 0,
      rangePreset: "all",
      rangeFrom: null,
      rangeTo: null,
      sheetPercent: 0,
      sheetReady: false,
      sheetExpiresAt: null,
    },
    push: null,
    error: { code: "CRM_APP_KEY_REJECTED", message: "App key rejected. Re-copy it from the ServiceTitan developer portal." },
    actions: { canDownload: false, canRetry: true, canCancel: false, canDelete: true, canUseForPush: false },
  },
  {
    ...baseRow,
    id: "6720000000000000000000a4",
    seq: 1043,
    kind: "push",
    status: "scheduled",
    stage: null,
    stageIndex: null,
    createdAt: "2026-10-15T09:11:00.000Z",
    startedAt: "2026-10-15T09:11:04.000Z",
    finishedAt: null,
    updatedAt: "2026-10-16T09:00:00.000Z",
    progress: { percent: 13.4 },
    pull: null,
    push: {
      sourceImportId: "6720000000000000000000a2",
      sourceSeq: 1039,
      totalRows: 3214,
      toCreate: 3180,
      created: 3180,
      failed: 0,
      posted: 425,
      perRun: 25,
      schedule: { enabled: true, frequency: { type: "daily", time: "09:00" }, timeZone: "America/Denver" },
      nextRunAt: "2026-10-17T15:00:00.000Z",
      employeeId: "6700000000000000000000a1",
    },
    actions: { canDownload: false, canRetry: false, canCancel: true, canDelete: false, canUseForPush: false },
  },
  {
    ...baseRow,
    crm: "housecallpro",
    crmLabel: "Housecall Pro",
    id: "6720000000000000000000a5",
    seq: 1044,
    kind: "pull",
    status: "done",
    stage: null,
    stageIndex: null,
    createdAt: "2026-10-17T09:11:00.000Z",
    startedAt: "2026-10-17T09:11:03.000Z",
    finishedAt: "2026-10-17T09:19:00.000Z",
    updatedAt: "2026-10-17T09:19:00.000Z",
    progress: { percent: 100 },
    pull: {
      totalJobs: 4,
      fetchedJobs: 4,
      totalPhotos: 12,
      cachedPhotos: 12,
      skippedPhotos: 0,
      processedPhotos: 12,
      failedPhotos: 0,
      skippedNoPhotos: 0,
      sheetRows: 4,
      rangePreset: "all",
      rangeFrom: null,
      rangeTo: null,
      sheetPercent: 100,
      sheetReady: true,
      sheetExpiresAt: "2026-11-16T09:19:00.000Z",
    },
    push: null,
    actions: { canDownload: true, canRetry: false, canCancel: false, canDelete: true, canUseForPush: true },
  },
];

export const fixturePushPreview: PushPreview = {
  push: {
    id: "6730000000000000000000a1",
    fileName: "import-1039-edited.xlsx",
    sourceImportId: "6720000000000000000000a2",
    business: { id: "6702000000000000000000c1", name: "Eagle Plumbing Repairs and Drain Cleaning" },
    owner: { email: "eagle@example.com" },
  },
  stats: {
    rows: 3214,
    toCreate: 3150,
    invalid: 12,
    alreadyImported: 0,
    duplicatesInSheet: 0,
    notIncluded: 0,
  },
  issues: [{ row: 18, code: "IMAGE_URL_NOT_ALLOWED", message: "image_2 is not on our storage" }],
  context: fixtureContext,
};

export const fixtureEstimate: PushEstimate = {
  mode: "scheduled",
  willCreate: 3180,
  perRun: 25,
  frequency: { type: "daily", time: "09:00" },
  timeZone: "America/Denver",
  runs: 128,
  firstRun: { date: "2026-10-17", time: "09:00", iso: "2026-10-17T15:00:00.000Z" },
  lastRun: { date: "2027-02-21", time: "09:00", iso: "2027-02-21T16:00:00.000Z" },
};

export const fixtureReview: ReviewResponse = {
  reviewToken: "mock-review-token",
  summary: {
    totalRows: 3214,
    willCreate: 3184,
    willSkip: 30,
    skipReasons: [
      { code: "no_category", count: 0, label: "No matching category and no default selected" },
      { code: "not_included", count: 18, label: "Marked include = N in the sheet" },
      { code: "already_imported", count: 12, label: "Already imported earlier" },
    ],
  },
  assumptions: [
    {
      key: "targets",
      title: "Where check-ins appear",
      severity: "info",
      count: null,
      details: null,
      text: "Check-ins are published to this business's website widgets only. Nothing is posted to Google, Facebook, Instagram, YouTube or TikTok.",
    },
    {
      key: "categories_matching",
      title: "Category matching",
      severity: "warn",
      count: 350,
      text: "Each row is matched to an existing category: 2,800 exact matches and 350 close (fuzzy) matches. Please check the fuzzy matches.",
      details: {
        total: 1,
        items: [{ sheetCategory: "Plumbing Service", categoryName: "Plumbing", method: "fuzzy", score: 0.72, count: 312 }],
      },
    },
    {
      key: "client_name",
      title: "Client name",
      severity: "warn",
      count: 3100,
      details: null,
      text: "The client name from the sheet is saved on each check-in. Edit or blank the client_name column in the sheet if it should not be shown.",
    },
    {
      key: "dates",
      title: "Dates",
      severity: "info",
      count: null,
      details: null,
      text: "Each check-in keeps its original job date as its date.",
    },
  ],
  estimate: fixtureEstimate,
};
